import { Badge } from "@/components/ui/badge";
import { timestampToUtcDateKey } from "@/lib/format";

export type BillStatus =
  | "draft"
  | "needs_review"
  | "pending_approval"
  | "approved"
  | "exported"
  | "paid"
  | "voided";

export const BILL_STATUS_LABELS: Record<BillStatus, string> = {
  draft: "Draft",
  needs_review: "Needs review",
  pending_approval: "Pending approval",
  approved: "Approved",
  exported: "Exported",
  paid: "Paid",
  voided: "Voided",
};

const BILL_STATUS_CLASSES: Record<BillStatus, string> = {
  draft: "bg-muted text-muted-foreground border-transparent",
  needs_review:
    "bg-amber-100 text-amber-900 border-transparent dark:bg-amber-950/60 dark:text-amber-200",
  pending_approval:
    "bg-blue-100 text-blue-900 border-transparent dark:bg-blue-950/60 dark:text-blue-200",
  approved:
    "bg-emerald-100 text-emerald-900 border-transparent dark:bg-emerald-950/60 dark:text-emerald-200",
  exported:
    "bg-violet-100 text-violet-900 border-transparent dark:bg-violet-950/60 dark:text-violet-200",
  paid: "bg-emerald-600 text-white border-transparent",
  voided: "bg-muted text-muted-foreground line-through border-transparent",
};

export function BillStatusBadge({ status }: { status: string }) {
  const key = status as BillStatus;
  return (
    <Badge
      variant="outline"
      className={BILL_STATUS_CLASSES[key] ?? "bg-muted text-muted-foreground"}
    >
      {BILL_STATUS_LABELS[key] ?? status}
    </Badge>
  );
}

/** Statuses in which a bill's fields, allocations, and documents are frozen. */
export const LOCKED_BILL_STATUSES: readonly string[] = [
  "pending_approval",
  "approved",
  "exported",
  "paid",
  "voided",
];

export function isBillLockedStatus(status: string): boolean {
  return LOCKED_BILL_STATUSES.includes(status);
}

/**
 * Parse a DatePicker `yyyy-MM-dd` value as UTC midnight. Bill dates are
 * date-only values persisted at `Date.UTC(y, m, d)` so the calendar day is
 * stable across user timezones.
 */
export function dateInputToUtcMs(value: string): number | undefined {
  const match = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return undefined;
  return Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

/** Inverse of {@link dateInputToUtcMs} for DatePicker `value`. */
export function utcMsToDateInput(ts: number | undefined): string {
  if (ts === undefined) return "";
  return timestampToUtcDateKey(ts);
}

export interface InternalProfileOption {
  _id: string;
  name?: string;
  email?: string;
  role: string;
}

export function internalProfileLabel(profile: InternalProfileOption): string {
  return profile.name || profile.email || profile._id;
}

/**
 * URL params ↔ listBills args, shared by the inbox and the detail page so the
 * detail page can reconstruct the exact working queue (prev/next, auto-advance)
 * from the query string the inbox carried over.
 */
export const BILL_FILTER_ALL = "__all__";

export interface BillListFilterArgs {
  status?: string;
  vendorId?: string;
  assigneeProfileId?: string;
  overdueOnly?: boolean;
  awaitingPayment?: boolean;
  exportProblems?: boolean;
  forAttentionProfileId?: string;
  searchText?: string;
}

export function parseBillListFilters(
  params: URLSearchParams,
  myProfileId?: string,
): BillListFilterArgs {
  const statusParam = params.get("status") ?? BILL_FILTER_ALL;
  const viewParam = params.get("view");
  const vendorParam = params.get("vendor") ?? BILL_FILTER_ALL;
  const assigneeParam = params.get("assignee") ?? BILL_FILTER_ALL;
  const searchParam = params.get("q") ?? "";
  return {
    // A pseudo-view crosses statuses, so it clears the status arg.
    status:
      viewParam || statusParam === BILL_FILTER_ALL ? undefined : statusParam,
    vendorId: vendorParam === BILL_FILTER_ALL ? undefined : vendorParam,
    assigneeProfileId:
      assigneeParam === BILL_FILTER_ALL ? undefined : assigneeParam,
    overdueOnly: params.get("overdue") === "1" || undefined,
    awaitingPayment: viewParam === "awaiting_payment" || undefined,
    exportProblems: viewParam === "export_problems" || undefined,
    forAttentionProfileId:
      params.get("attention") === "1" && myProfileId ? myProfileId : undefined,
    searchText: searchParam || undefined,
  };
}

export function hasActiveBillListFilters(params: URLSearchParams): boolean {
  return ["status", "view", "vendor", "assignee", "overdue", "attention", "q"]
    .some((key) => params.get(key));
}

/**
 * Compact text badges for high-stakes row flags. Deliberately text, not
 * icon-only — the inbox must stay scannable without a legend.
 */
export function OpenQuestionFlagBadge() {
  return (
    <Badge
      variant="outline"
      className="h-4 rounded px-1 text-[10px] font-medium leading-none border-blue-400 bg-blue-100 text-blue-900 dark:border-blue-800/60 dark:bg-blue-950/60 dark:text-blue-200"
    >
      Q&nbsp;open
    </Badge>
  );
}

/**
 * Parse a money input string; returns undefined for blank/invalid values.
 * Accepts US human forms — "1234.56", "1,234.56", "$500" — and deliberately
 * REJECTS ambiguous ones (European "1.234,56", "1 234") rather than
 * silently mangling them into a different number.
 */
export function parseAmountInput(value: string): number | undefined {
  const trimmed = value.trim().replace(/^\$\s?/, "");
  if (!trimmed) return undefined;
  if (!/^(\d{1,3}(,\d{3})+|\d+)(\.\d+)?$/.test(trimmed)) return undefined;
  return Number(trimmed.replace(/,/g, ""));
}

/** Round to cents to compare allocation sums against the bill total. */
export function roundToCents(value: number): number {
  return Math.round(value * 100) / 100;
}

/**
 * Whether an extracted currency means USD. Models report what is printed on
 * the invoice, which is usually a symbol rather than the ISO code — treating
 * "$" as foreign produced a false "bills must be USD" warning on ordinary
 * domestic invoices.
 */
export function isUsdCurrency(value: string | undefined): boolean {
  if (!value) return false;
  const normalized = value.trim().toUpperCase();
  return normalized === "USD" || normalized === "$" || normalized === "US$";
}
